import { useMemo, useState } from 'react';
import { useFetchDefaultPinnedView } from 'customer-data-views-management/pinnedViews/hooks/useFetchDefaultPinnedView';
import { useFetchPinnedViews } from 'customer-data-views-management/pinnedViews/hooks/useFetchPinnedViews';
import { useFetchSingleView } from 'customer-data-views-management/singleView/hooks/useFetchSingleView';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { convertCustomerDataViewToView } from '../utils/mapper';
import { useHasViews, useViewPropsCurrentViewId, useViewsNamespace } from './useViewProps';
function useInitialViewId({
  currentViewId,
  objectTypeId,
  namespace
}) {
  const [initialViewId, setInitialViewId] = useState(currentViewId);
  if (!initialViewId && currentViewId) {
    setInitialViewId(currentViewId);
  }
  const [prevObjectTypeId, setPrevObjectTypeId] = useState(objectTypeId);
  if (prevObjectTypeId !== objectTypeId) {
    setPrevObjectTypeId(objectTypeId);
    setInitialViewId(currentViewId);
  }
  const [prevNamespace, setPrevNamespace] = useState(namespace);
  if (prevNamespace !== namespace) {
    setPrevNamespace(namespace);
    setInitialViewId(currentViewId);
  }
  return initialViewId;
}
export function useFetchCurrentView() {
  var _pinnedViewsData$pinn;
  const objectTypeId = useObjectTypeId();
  const namespace = useViewsNamespace();
  const hasViews = useHasViews();
  const isFoldersMode = useIsFoldersMode();
  const currentViewIdFromProps = useViewPropsCurrentViewId();
  const initialViewId = useInitialViewId({
    currentViewId: currentViewIdFromProps,
    objectTypeId,
    namespace
  });
  const {
    data: defaultPinnedViewData,
    loading: loadingDefaultPinnedView,
    error: defaultPinnedViewError
  } = useFetchDefaultPinnedView({
    objectTypeId,
    namespace,
    viewId: initialViewId,
    skip: !hasViews
  });
  const {
    data: currentViewData,
    loading: loadingCurrentView,
    error: currentViewError
  } = useFetchSingleView({
    objectTypeId,
    namespaceName: namespace,
    // falsey id should skip the query
    id: hasViews ? currentViewIdFromProps : undefined
  });
  const {
    data: pinnedViewsData
  } = useFetchPinnedViews({
    objectTypeId,
    namespace
  });
  const {
    data: fallbackDefaultPinnedViewData
  } = useFetchSingleView({
    objectTypeId,
    namespaceName: namespace,
    id: defaultPinnedViewError ? pinnedViewsData === null || pinnedViewsData === void 0 || (_pinnedViewsData$pinn = pinnedViewsData.pinnedViews) === null || _pinnedViewsData$pinn === void 0 ? void 0 : _pinnedViewsData$pinn[0] : undefined
  });
  const defaultPinnedView = useMemo(() => {
    const rawDefaultPinnedView = (defaultPinnedViewData === null || defaultPinnedViewData === void 0 ? void 0 : defaultPinnedViewData.defaultPinnedView) || (fallbackDefaultPinnedViewData === null || fallbackDefaultPinnedViewData === void 0 ? void 0 : fallbackDefaultPinnedViewData.singleViewById);
    return rawDefaultPinnedView ? convertCustomerDataViewToView(rawDefaultPinnedView) : undefined;
  }, [defaultPinnedViewData, fallbackDefaultPinnedViewData]);
  const currentView = useMemo(() => {
    return currentViewData !== null && currentViewData !== void 0 && currentViewData.singleViewById ? convertCustomerDataViewToView(currentViewData.singleViewById) : undefined;
  }, [currentViewData]);
  if (!hasViews || isFoldersMode) {
    return {
      loading: false,
      currentView: undefined
    };
  }
  if (!currentViewIdFromProps) {
    return {
      loading: loadingDefaultPinnedView,
      currentView: defaultPinnedView
    };
  }

  // Handle invalid current view id prop
  if (currentViewError && defaultPinnedView) {
    return {
      loading: false,
      currentView: defaultPinnedView
    };
  }
  return {
    loading: loadingCurrentView,
    currentView
  };
}